import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { startUpload, cancelActiveUpload, UPLOAD_PENDING_STORAGE_KEY } from '../../services/uploadService';

const UPLOAD_COMPLETE_DISMISS_MS = 4000;
const UploadContext = createContext(null);

export function UploadProvider({ children }) {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadPercentage, setUploadPercentage] = useState(0);
    const [uploadDisplayName, setUploadDisplayName] = useState('');
    const [isUploadComplete, setIsUploadComplete] = useState(false);
    const [hasUploadError, setHasUploadError] = useState(false);
    const [isUploadForegrounded, setIsUploadForegrounded] = useState(false);
    const isUploadingRef = useRef(false);
    const hasUploadErrorRef = useRef(false);
    const isCancelledRef = useRef(false);

    const dismissUploadBar = useCallback(() => {
        if (isUploadingRef.current) return;
        setIsUploadComplete(false);
        setHasUploadError(false);
        setUploadPercentage(0);
        setUploadDisplayName('');
    }, []);

    useEffect(() => {
        if (!isUploadComplete) return;
        const timer = setTimeout(dismissUploadBar, UPLOAD_COMPLETE_DISMISS_MS);
        return () => clearTimeout(timer);
    }, [isUploadComplete, dismissUploadBar]);

    const triggerUpload = useCallback(({ auth, storage, files, displayName, sex, tumor, examDate }) => {
        if (isUploadingRef.current) {
            console.warn('An upload is already in progress.');
            return;
        }
        isUploadingRef.current = true;
        hasUploadErrorRef.current = false;
        isCancelledRef.current = false;
        localStorage.setItem(UPLOAD_PENDING_STORAGE_KEY, '1');
        setIsUploading(true);
        setUploadPercentage(0);
        setUploadDisplayName(displayName);
        setIsUploadComplete(false);
        setHasUploadError(false);

        startUpload({
            auth,
            storage,
            files,
            displayName,
            sex,
            tumor,
            examDate,
            callbacks: {
                onProgress: (pct) => {
                    if (isCancelledRef.current) return;
                    setUploadPercentage(pct);
                },
                onComplete: (isEmpty = false) => {
                    if (isCancelledRef.current) return;
                    if (hasUploadErrorRef.current) return;
                    localStorage.removeItem(UPLOAD_PENDING_STORAGE_KEY);
                    isUploadingRef.current = false;
                    if (isEmpty) {
                        setIsUploading(false);
                        setUploadDisplayName('');
                        setUploadPercentage(0);
                    } else {
                        setIsUploading(false);
                        setIsUploadComplete(true);
                    }
                },
                onError: () => {
                    if (isCancelledRef.current) return;
                    localStorage.removeItem(UPLOAD_PENDING_STORAGE_KEY);
                    hasUploadErrorRef.current = true;
                    isUploadingRef.current = false;
                    setIsUploading(false);
                    setUploadPercentage(0);
                    setHasUploadError(true);
                },
            },
        });
    }, []);

    /**
     * Cancels the in-progress upload (if any): hides/resets the upload UI
     * immediately, then cancels the underlying Storage upload and marks the
     * patient record as failed. Must be awaited before invalidating auth
     * (e.g. before `signOut`) so the cleanup write still has a valid session.
     * No-op when no upload is in progress.
     * @param {string} [uid]
     * @returns {Promise<void>}
     */
    const cancelUpload = useCallback(async (uid) => {
        if (!isUploadingRef.current) return;

        isCancelledRef.current = true;
        isUploadingRef.current = false;
        hasUploadErrorRef.current = false;
        localStorage.removeItem(UPLOAD_PENDING_STORAGE_KEY);
        setIsUploading(false);
        setUploadPercentage(0);
        setUploadDisplayName('');
        setIsUploadComplete(false);
        setHasUploadError(false);

        try {
            await cancelActiveUpload(uid);
        } catch (err) {
            console.warn('Failed to cancel active upload:', err);
        }
    }, []);

    return (
        <UploadContext.Provider value={{
            isUploading,
            uploadPercentage,
            uploadDisplayName,
            isUploadComplete,
            hasUploadError,
            isUploadForegrounded,
            setIsUploadForegrounded,
            triggerUpload,
            cancelUpload,
            dismissUploadBar,
        }}>
            {children}
        </UploadContext.Provider>
    );
}

UploadProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export function useUpload() {
    const ctx = useContext(UploadContext);
    if (!ctx) throw new Error('useUpload must be used within UploadProvider');
    return ctx;
}

export { UploadContext };
